/**
 * Link Utilities
 * Helper functions for managing inline links in the editor
 */
import { Editor, Node, Path, Range, Element as SlateElement, Transforms } from 'slate';
import { CustomText, LinkElement } from '../../../types/slate';

/**
 * Normalize URL by adding https:// if no protocol is present
 */
export const normalizeUrl = (url: string) => {
  if (!url) {
    return url;
  }
  if (/^https?:\/\//i.test(url)) {
    return url;
  }
  if (/^www\./i.test(url)) {
    return `https://${url}`;
  }
  return `https://${url}`;
};

/**
 * Check if the current selection is inside a link
 */
export const isLinkActive = (editor: Editor) => {
  const [link] = Array.from(
    Editor.nodes(editor, {
      match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'link',
    })
  );
  return Boolean(link);
};

/**
 * Get the active link node and path if cursor is inside a link
 */
export const getActiveLink = (editor: Editor) => {
  const [link] = Array.from(
    Editor.nodes(editor, {
      match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'link',
    })
  );
  return link;
};

/**
 * Remove link formatting from the current selection
 */
export const unwrapLink = (editor: Editor) => {
  Transforms.unwrapNodes(editor, {
    match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'link',
  });
};

/**
 * Wrap the current selection in a link
 */
export const wrapLink = (editor: Editor, url: string) => {
  if (isLinkActive(editor)) {
    unwrapLink(editor);
  }

  const { selection } = editor;
  const isCollapsed = selection && Range.isCollapsed(selection);
  const normalizedUrl = normalizeUrl(url);

  const link = {
    type: 'link',
    url: normalizedUrl,
    children: isCollapsed ? [{ text: url }] : [],
  } as LinkElement;

  if (isCollapsed) {
    Transforms.insertNodes(editor, link);
  } else {
    Transforms.wrapNodes(editor, link, { split: true });
    Transforms.collapse(editor, { edge: 'end' });
  }
};

/**
 * Insert a new link with specified text and URL
 */
export const insertLink = (editor: Editor, url: string, text: string) => {
  if (!url) {
    return;
  }

  const normalizedUrl = normalizeUrl(url);
  const { selection } = editor;

  if (selection && !Range.isCollapsed(selection)) {
    // If there's a selection, wrap it with a link
    wrapLink(editor, url);
  } else if (text) {
    // If we have text, insert a new link with that text
    const link = {
      type: 'link',
      url: normalizedUrl,
      children: [{ text }],
    } as LinkElement;
    Transforms.insertNodes(editor, link);
    // Move cursor after the link
    Transforms.move(editor);
  } else {
    // Just insert URL as link
    wrapLink(editor, url);
  }
};

/**
 * Update the URL and optionally the text of an existing link
 */
export const updateLink = (
  editor: Editor,
  url: string,
  linkPath: Path,
  newText: string | null = null
) => {
  const normalizedUrl = normalizeUrl(url);

  // Update the URL
  Transforms.setNodes(editor, { url: normalizedUrl }, { at: linkPath });

  // Update the text if provided
  if (newText !== null) {
    // Get the current text node inside the link
    const linkNode = Node.get(editor, linkPath);
    const currentText = Node.string(linkNode);

    if (newText !== currentText) {
      // Select all text inside the link and replace it
      try {
        Transforms.delete(editor, { at: linkPath });
        Transforms.insertNodes(
          editor,
          {
            type: 'link',
            url: normalizedUrl,
            children: [{ text: newText }],
          },
          { at: linkPath }
        );
      } catch {
        // If deletion fails, just update the URL
      }
    }
  }
};

// URL detection regex for decorator
const URL_REGEX =
  /https?:\/\/[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/gi;

/**
 * Decorator function to detect URLs in text and style them as links
 */
export const createLinkDecorator = () => {
  return ([node, path]: [CustomText, Path]) => {
    const ranges = [];

    // Reset regex state
    URL_REGEX.lastIndex = 0;
    const match = URL_REGEX.exec(node.text);

    while (match !== null) {
      const start = match.index;
      const end = start + match[0].length;

      ranges.push({
        anchor: { path, offset: start },
        focus: { path, offset: end },
        decoration: 'link',
      });
    }

    return ranges;
  };
};
